<template>
    <b-col cols="12" class="d-flex align-items-stretch flex-fill mt-3 mb-2">
        <b-card class="flex-fill">
            <b-col cols="12">
                <b-row cols="12" class="mb-1">
                    <b-col><p class="text-uppercase font-weight-bold text-danger  device-warning-title">
                        {{ $t('Device warnings component') }}</p>
                    </b-col>
                </b-row>

                <router-link v-for="warning in warnings"
                             class="d-block mb-3"
                             :to="warning.to"
                             :key="warning.id"
                             @click.prevent="warningClickHandler(warning)">
                    <b-row cols="12" class="danger-text text-black">
                        <div class="bg-danger warning-icon d-flex justify-content-center align-items-center mb-4">
                            <img :src="warning.icon" class="" alt="">
                        </div>
                        <div class="ml-20">
                            <b-spinner v-if="isLoading" class="align-middle mx-2 mt-2"></b-spinner>
                            <div v-else>
                                <div class="pl-3">
                                    <span class="font-weight-bold mr-2 align-top">
                                        {{ warning.count }}
                                    </span>

                                    <div class="d-inline-block align-top">
                                        <div> {{ $t(warning.label) }}</div>
                                        <div class="text-muted small"> {{ getDescription(warning.id) }}</div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </b-row>
                </router-link>

            </b-col>
        </b-card>
    </b-col>
</template>

<script>
import {capitalize} from "lodash";
import {DeviceStatesEnum, RTStatesEnum} from "../../js/enums";

export default {
    name: "DeviceWarnings",
    data() {
        return {
            isLoading: true,
            dgfeWarning: {
                id: 1,
                count: 0,
                to: {
                    name: 'DevicesContainer',
                    query: {
                        dgfe_perc_free: 5,
                        selected_state: ''
                    }
                },
                icon: "/img/icons/sd-card.svg",
                label: 'DGFE memory almost full',
            },
            periodicVerificationWarning: {
                id: 2,
                count: 0,
                to: {
                    name: 'DevicesContainer',
                    query: {
                        last_verification_date_months: 0,
                        selected_state: ''
                    }
                },
                icon: "/img/icons/shield-cross.svg",
                label: 'Warning verification is due to expire'
            },
            closureWorning: {
                id: 3,
                count: 0,
                to: {
                    name: 'DevicesContainer',
                    query: {
                        max_remaining_clousures: 29,
                        selected_state: ''
                    }
                },
                icon: "/img/icons/memory.svg",
                label: 'Fiscal memory almost full'
            },
            rtCertificatesWarning: {
                id: 4,
                count: 0,
                to: {
                    name: 'DevicesContainer',
                    query: {
                        cert_expire_date_months: 1,
                        selected_state: ''
                    }
                },
                icon: "/img/icons/shield-security.svg",
                label: 'RT certificates expiring'
            },
        }
    },
    mounted() {
        this.loadAlertsData();
    },
    methods: {
        loadAlertsData() {
            let dgfeUrl = `${this.$store.getters.getAppUrl}/dealer/api/v2/alerts/dgfe`;
            let periodicVerificationUrl = `${this.$store.getters.getAppUrl}/dealer/api/v2/alerts/periodic-verification`;
            let closuresUrl = `${this.$store.getters.getAppUrl}/dealer/api/v2/alerts/closures`;
            let rtCertificates = `${this.$store.getters.getAppUrl}/dealer/api/v2/alerts/rt-certificates`;

            return axios.all([
                axios.get(dgfeUrl),
                axios.get(periodicVerificationUrl),
                axios.get(closuresUrl),
                axios.get(rtCertificates),
            ]).then(axios.spread((dgfeData, periodicVerificationData, closuresData, rtCertificatesData) => {
                this.updateAlerts({dgfeData, periodicVerificationData, closuresData, rtCertificatesData});
            })).finally(() => {
                this.isLoading = false;
            });

        },
        updateAlerts({dgfeData, periodicVerificationData, closuresData, rtCertificatesData}) {
            this.dgfeWarning.count = dgfeData.data.data.count;
            this.dgfeWarning.to.query.dgfe_perc_free = dgfeData.data.data.dashboard_setting;
            this.dgfeWarning.to.query.selected_state = dgfeData.data.data?.selected_state?.join(',') || this.defaultSelectedState;

            this.periodicVerificationWarning.count = periodicVerificationData.data.data.count;
            this.periodicVerificationWarning.to.query.last_verification_date_months = periodicVerificationData.data.data.dashboard_setting;
            this.periodicVerificationWarning.to.query.selected_state = periodicVerificationData.data.data?.selected_state?.join(',') || this.defaultSelectedState;

            this.closureWorning.count = closuresData.data.data.count;
            this.closureWorning.to.query.max_remaining_clousures = closuresData.data.data.dashboard_setting;
            this.closureWorning.to.query.selected_state = closuresData.data.data?.selected_state?.join(',') || this.defaultSelectedState;

            this.rtCertificatesWarning.count = rtCertificatesData.data.data.count;
            this.rtCertificatesWarning.to.query.cert_expire_date_months = rtCertificatesData.data.data.dashboard_setting;
            this.rtCertificatesWarning.to.query.selected_state = rtCertificatesData.data.data?.selected_state?.join(',') || this.defaultSelectedState;
        },
        getDescription(id) {
            switch (id) {
                case 1:
                    return capitalize(this.$t('less than {value}', {value: this.dgfeWarning.to.query.dgfe_perc_free + '%'}));
                case 2:
                    const now = new Date();
                    const currentMonth = now.getMonth(); // 0-based index
                    const currentYear = now.getFullYear();

                    const date = new Date(currentYear, currentMonth + (+this.periodicVerificationWarning.to.query.last_verification_date_months));
                    const monthNumber = date.getMonth() + 1; // 1-based
                    const year = date.getFullYear();

                    const monthName = this.$t('months.' + monthNumber);

                    return capitalize(this.$t('by {month}', {month: `${monthName}`}) + ` ${year}`);
                case 3:
                    return capitalize(this.$t("Less than {count} closures remaining", {count: this.closureWorning.to.query.max_remaining_clousures + 1}));
                case 4:
                    const count = this.rtCertificatesWarning.to.query.cert_expire_date_months;
                    return capitalize(this.$tc("Within {count} month(s)", count, {count}));
                default:
                    return '';
            }
        },
        warningClickHandler(warning) {
            if (!warning.to) return;
            this.$router.push(warning.to);
        }
    },
    computed: {
        warnings() {
            return [
                this.dgfeWarning,
                this.periodicVerificationWarning,
                this.closureWorning,
                this.rtCertificatesWarning,
            ]
        },
        defaultSelectedState() {
            return [...RTStatesEnum].filter(v => v = !RTStatesEnum.DISMESSO).join(',')
        }
    }
}
</script>

<style scoped>
.device-warning-title {
    font-size: 20px
}

.danger-text {
    font-size: 16px
}

.warning-icon {
    border-radius: 5px;
    width: 40px;
    height: 40px;
}

.warning-icon {
    border-radius: 5px;
    width: 40px;
    height: 40px;
}

</style>
